import React, { useState, useMemo } from 'react';
import {
  Dialog,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Card,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  TablePagination,
  TextField,
  InputAdornment,
  Button,
  Collapse,
  Tooltip as MuiTooltip,
  alpha,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from 'recharts';

import {
  buildPalmtopAnalytics,
  fmtNum,
  fmtDate,
  fmtPct,
  ymShort,
  ymLong,
  packingLabel,
} from '../services/palmtopAnalytics';

// Colour tokens from the original Backlog Manifest report, per theme mode.
const TOKENS = {
  light: {
    accent: '#2a78d6',
    accent2: '#eb6834',
    good: '#0ca30c',
    warning: '#fab219',
    serious: '#ec835a',
    critical: '#d03b3b',
    seqLow: '#9ec5f4',
    seqHigh: '#104281',
    muted: '#898781',
  },
  dark: {
    accent: '#3987e5',
    accent2: '#d95926',
    good: '#0ca30c',
    warning: '#fab219',
    serious: '#ec835a',
    critical: '#d03b3b',
    seqLow: '#184f95',
    seqHigh: '#9ec5f4',
    muted: '#8f8d87',
  },
};

// Same numeric-borderRadius caveat as FinanceDashboard: the app theme's
// shape.borderRadius is 25, so panels use explicit px.
const RADIUS = { panel: '12px', inset: '8px' };

const MONO = 'ui-monospace, "SFMono-Regular", Menlo, Consolas, monospace';

const SEVERITY_TONE = {
  Critical: 'critical',
  Serious: 'serious',
  Warning: 'warning',
  Informational: 'accent',
};

const useTokens = () => {
  const theme = useTheme();
  return TOKENS[theme.palette.mode === 'dark' ? 'dark' : 'light'];
};

const lerpColor = (a, b, t) => {
  const p = (h) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
  const [c1, c2] = [p(a), p(b)];
  return `#${c1
    .map((v, i) => Math.round(v + (c2[i] - v) * t).toString(16).padStart(2, '0'))
    .join('')}`;
};

const ageTone = (days) => {
  if (days <= 30) return 'good';
  if (days <= 90) return 'warning';
  if (days <= 180) return 'serious';
  return 'critical';
};

// Renders "**bold**" segments produced by the rule-based briefing.
const Rich = ({ text }) =>
  text.split('**').map((seg, i) =>
    i % 2 ? (
      <Box component="strong" key={i} sx={{ color: 'text.primary', fontWeight: 700 }}>
        {seg}
      </Box>
    ) : (
      <React.Fragment key={i}>{seg}</React.Fragment>
    )
  );

// ---------- building blocks ----------

const Panel = ({ children, sx }) => {
  const theme = useTheme();
  return (
    <Card
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: RADIUS.panel,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.background.paper,
        ...sx,
      }}
    >
      {children}
    </Card>
  );
};

const PanelHead = ({ title, caption }) => (
  <Box sx={{ mb: 1.75 }}>
    <Typography sx={{ fontSize: 15, fontWeight: 700 }}>{title}</Typography>
    {caption && (
      <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.25 }}>{caption}</Typography>
    )}
  </Box>
);

const SectionTitle = ({ children, sx }) => (
  <Typography
    sx={{
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      color: 'text.secondary',
      fontFamily: MONO,
      mb: 1.5,
      ...sx,
    }}
  >
    {children}
  </Typography>
);

const Kpi = ({ label, value, sub, tone }) => {
  const theme = useTheme();
  const tk = useTokens();
  const accent = tone === 'neutral' ? tk.accent : tk[tone];
  return (
    <Card
      elevation={0}
      sx={{
        p: 2,
        borderRadius: RADIUS.panel,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.background.paper,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 3,
          bgcolor: accent,
        },
      }}
    >
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: 0.6,
          textTransform: 'uppercase',
          color: 'text.secondary',
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontSize: 28, fontWeight: 700, lineHeight: 1.15, fontFamily: MONO }}>
        {value}
      </Typography>
      <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5, lineHeight: 1.45 }}>
        {sub}
      </Typography>
    </Card>
  );
};

const Tag = ({ children, tone, solid = false }) => {
  const tk = useTokens();
  const c = tk[tone] || tk.muted;
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        px: 0.9,
        py: 0.15,
        borderRadius: '999px',
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.3,
        color: solid ? '#fff' : c,
        bgcolor: solid ? c : alpha(c, 0.14),
        border: `1px solid ${alpha(c, 0.35)}`,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </Box>
  );
};

/**
 * Ranked horizontal bars. Colour is sequential by value unless `colors` gives
 * one per row (the ageing chart uses severity colours instead).
 */
const BarList = ({ items, colors, valueLabel = 'Outstanding qty', nLabel = 'Shipments', format = fmtNum, max: maxProp }) => {
  const theme = useTheme();
  const tk = useTokens();
  const max = maxProp ?? Math.max(...items.map((d) => d.v));
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.1 }}>
      {items.map((d, i) => {
        const t = max ? d.v / max : 0;
        const color = colors ? colors[i] : lerpColor(tk.seqLow, tk.seqHigh, Math.max(0.12, t));
        return (
          <Box
            key={d.label}
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '96px 1fr 92px', sm: '170px 1fr 124px' },
              alignItems: 'center',
              gap: 1.25,
            }}
          >
            <Typography
              noWrap
              title={d.label}
              sx={{ fontSize: 12.5, color: 'text.secondary' }}
            >
              {d.label}
            </Typography>
            <Box
              sx={{
                height: 14,
                borderRadius: '4px',
                bgcolor: alpha(theme.palette.text.secondary, 0.08),
                overflow: 'hidden',
              }}
            >
              <MuiTooltip
                arrow
                title={
                  <Box sx={{ fontSize: 12 }}>
                    <Box sx={{ fontWeight: 700, mb: 0.25 }}>{d.label}</Box>
                    <Box>
                      {valueLabel}: {d.text || format(d.v)}
                    </Box>
                    {d.n !== undefined && (
                      <Box>
                        {nLabel}: {fmtNum(d.n)}
                      </Box>
                    )}
                  </Box>
                }
              >
                <Box
                  sx={{
                    height: '100%',
                    width: `${Math.max(t * 100, 0.6)}%`,
                    bgcolor: color,
                    borderRadius: '4px',
                    cursor: 'default',
                  }}
                />
              </MuiTooltip>
            </Box>
            <Typography sx={{ fontSize: 12, fontFamily: MONO, textAlign: 'right' }}>
              {d.text || format(d.v)}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

// ---------- sections ----------

const Header = ({ a }) => {
  const tk = useTokens();
  const { data: d, meta, concentration } = a;
  const pb = d.production_backlog;
  const metaItems = [
    ['Snapshot', fmtDate(meta.snapshotDate)],
    [fmtNum(d.total_rows), 'planned shipment lines'],
    ['Coverage', meta.coverage],
  ];
  return (
    <Box>
      <Typography
        sx={{
          fontFamily: MONO,
          fontSize: 11.5,
          fontWeight: 600,
          letterSpacing: '0.11em',
          textTransform: 'uppercase',
          color: tk.accent,
        }}
      >
        Palmtop · Master Plan
      </Typography>
      <Typography
        component="h1"
        sx={{
          fontSize: { xs: 26, sm: 34, md: 40 },
          fontWeight: 700,
          lineHeight: 1.1,
          letterSpacing: '-0.01em',
          my: 1.25,
          textWrap: 'balance',
        }}
      >
        Shipping plan health and backlog risk
      </Typography>
      <Typography sx={{ maxWidth: '64ch', color: 'text.secondary', fontSize: 15, lineHeight: 1.6 }}>
        The master plan tracks {fmtNum(d.total_rows)} export shipment lines from contract to packing. Two
        backlogs stand out: {fmtNum(d.atrisk_rows)} lines in production with {fmtNum(d.atrisk_qty)} units
        still unpacked past their required date — almost all at {concentration.main.si_warehouse} — and{' '}
        {fmtNum(pb.n)} booked lines that never received a production order. This page shows where each one is
        stuck and what to do about it.
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
        {metaItems.map(([k, v]) => (
          <Box
            key={k}
            sx={{
              display: 'flex',
              gap: 0.75,
              alignItems: 'baseline',
              px: 1.25,
              py: 0.5,
              borderRadius: '999px',
              border: 1,
              borderColor: 'divider',
              fontSize: 12.5,
            }}
          >
            <Box component="span" sx={{ fontWeight: 700, fontFamily: MONO }}>
              {k}
            </Box>
            <Box component="span" sx={{ color: 'text.secondary' }}>
              {v}
            </Box>
          </Box>
        ))}
        <Box
          sx={{
            px: 1.25,
            py: 0.5,
            borderRadius: '999px',
            bgcolor: alpha(tk.accent, 0.12),
            color: tk.accent,
            fontSize: 12.5,
            fontWeight: 600,
          }}
        >
          At risk = unpacked qty &gt; 0 and required date passed
        </Box>
      </Box>
    </Box>
  );
};

const ConcentrationStrip = ({ a }) => {
  const theme = useTheme();
  const tk = useTokens();
  const { main, mainPct, others, othersQty } = a.concentration;
  return (
    <Panel>
      <PanelHead
        title="At-risk volume by warehouse"
        caption={`${fmtNum(a.data.atrisk_rows)} shipments · ${fmtNum(a.data.atrisk_qty)} units outstanding`}
      />
      <Box
        sx={{
          display: 'flex',
          height: 34,
          borderRadius: RADIUS.inset,
          overflow: 'hidden',
          bgcolor: alpha(theme.palette.text.secondary, 0.08),
        }}
      >
        <MuiTooltip
          arrow
          title={`${main.si_warehouse}: ${fmtNum(main.v)} units · ${fmtNum(main.n)} shipments`}
        >
          <Box
            sx={{
              width: `${mainPct}%`,
              bgcolor: tk.accent,
              color: '#fff',
              fontSize: 12.5,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              px: 1.5,
              whiteSpace: 'nowrap',
            }}
          >
            {main.si_warehouse} · {fmtNum(main.v)} units ({mainPct.toFixed(1)}%)
          </Box>
        </MuiTooltip>
        <MuiTooltip
          arrow
          title={others.map((o) => `${o.si_warehouse}: ${fmtNum(o.v)} units · ${fmtNum(o.n)} shipments`).join(' · ')}
        >
          <Box sx={{ flex: 1, minWidth: 6, bgcolor: tk.muted }} />
        </MuiTooltip>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5, mt: 1.25, fontSize: 12.5, color: 'text.secondary' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: tk.accent }} />
          {main.si_warehouse} — {fmtNum(main.v)} units ({mainPct.toFixed(1)}%)
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: tk.muted }} />
          Other {others.length} sites — {fmtNum(othersQty)} units ({(100 - mainPct).toFixed(1)}%)
        </Box>
      </Box>
    </Panel>
  );
};

// Each risk sits on the same row as the action that addresses it. On narrow
// screens the pair stacks, with the arrow pointing down instead of across.
const Briefing = ({ a }) => {
  const theme = useTheme();
  const tk = useTokens();
  const colHead = {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: 'text.secondary',
    fontFamily: MONO,
  };
  return (
    <Panel>
      <PanelHead
        title="Risks & actions"
        caption="Rule-based: every figure is computed directly from the master plan export, ordered by severity."
      />
      <Box
        sx={{
          display: { xs: 'none', md: 'grid' },
          gridTemplateColumns: '1fr 32px 1fr',
          mb: 1,
        }}
      >
        <Typography sx={colHead}>Risk</Typography>
        <Box />
        <Typography sx={colHead}>Recommended action</Typography>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {a.findings.map((f, i) => {
          const c = tk[SEVERITY_TONE[f.severity]];
          return (
            <Box
              key={f.title}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 32px 1fr' },
                alignItems: 'stretch',
              }}
            >
              <Box
                sx={{
                  p: 1.75,
                  borderRadius: RADIUS.inset,
                  border: `1px solid ${theme.palette.divider}`,
                  borderLeft: `3px solid ${c}`,
                  bgcolor: alpha(c, 0.04),
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, flexWrap: 'wrap' }}>
                  <Tag tone={SEVERITY_TONE[f.severity]}>{f.severity}</Tag>
                  <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{f.title}</Typography>
                </Box>
                <Typography sx={{ fontSize: 13, color: 'text.secondary', lineHeight: 1.6 }}>
                  <Rich text={f.text} />
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: c,
                  py: { xs: 0.25, md: 0 },
                }}
              >
                <ArrowForwardIcon sx={{ fontSize: 20, transform: { xs: 'rotate(90deg)', md: 'none' } }} />
              </Box>

              <Box
                sx={{
                  p: 1.75,
                  borderRadius: RADIUS.inset,
                  border: `1px solid ${alpha(tk.accent, 0.3)}`,
                  bgcolor: alpha(tk.accent, 0.06),
                  display: 'flex',
                  gap: 1.25,
                  alignItems: 'flex-start',
                }}
              >
                <Box
                  sx={{
                    flex: '0 0 24px',
                    height: 24,
                    borderRadius: '50%',
                    bgcolor: alpha(tk.accent, 0.16),
                    color: tk.accent,
                    fontSize: 12,
                    fontWeight: 700,
                    fontFamily: MONO,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {i + 1}
                </Box>
                <Typography sx={{ fontSize: 13.5, lineHeight: 1.6, color: 'text.secondary' }}>
                  <Box component="strong" sx={{ color: 'text.primary' }}>
                    {f.action.lead}
                  </Box>{' '}
                  {f.action.rest}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Panel>
  );
};

const TrendChart = ({ a }) => {
  const theme = useTheme();
  const tk = useTokens();
  const [showTable, setShowTable] = useState(false);
  const { rows, shiftYm, yMax } = a.trend;
  const axis = {
    tick: { fill: theme.palette.text.secondary, fontSize: 11 },
    stroke: alpha(theme.palette.text.secondary, 0.3),
    tickLine: false,
  };
  const ticks = Array.from({ length: yMax / 4 + 1 }, (_, i) => i * 4);

  return (
    <Panel>
      <PanelHead
        title="Backlog rate as a share of planned volume, by month"
        caption={`Unpacked-and-overdue quantity ÷ that month's total planned quantity. Dashed line marks the ${ymShort(shiftYm)} step change.`}
      />
      <Box sx={{ width: '100%', height: 280 }}>
        <ResponsiveContainer>
          <AreaChart data={rows} margin={{ top: 10, right: 16, left: -8, bottom: 0 }}>
            <defs>
              <linearGradient id="palmtopRate" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={tk.accent} stopOpacity={0.28} />
                <stop offset="100%" stopColor={tk.accent} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} stroke={alpha(theme.palette.text.secondary, 0.15)} />
            <XAxis dataKey="ym" tickFormatter={ymShort} {...axis} interval={2} />
            <YAxis domain={[0, yMax]} ticks={ticks} tickFormatter={(v) => `${v}%`} {...axis} axisLine={false} />
            <Tooltip
              contentStyle={{
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 8,
                fontSize: 12,
                color: theme.palette.text.primary,
              }}
              labelFormatter={ymLong}
              formatter={(v, _n, { payload }) => [
                `${fmtNum(payload.atrisk_qty)} of ${fmtNum(payload.planned_qty)} planned units (${v}%)`,
                'Backlog rate',
              ]}
            />
            <ReferenceLine
              x={shiftYm}
              stroke={tk.accent2}
              strokeDasharray="5 4"
              label={{
                value: `${ymShort(shiftYm)} — rate steps up`,
                position: 'insideTopRight',
                fill: tk.accent2,
                fontSize: 11.5,
                fontWeight: 600,
              }}
            />
            <Area
              type="monotone"
              dataKey="rate_pct"
              stroke={tk.accent}
              strokeWidth={2.25}
              fill="url(#palmtopRate)"
              dot={{ r: 3, fill: tk.accent, strokeWidth: 0 }}
              activeDot={{ r: 5 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </Box>

      <Button
        size="small"
        onClick={() => setShowTable((s) => !s)}
        endIcon={
          <ExpandMoreIcon sx={{ transform: showTable ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
        }
        sx={{ mt: 1, textTransform: 'none', fontSize: 12.5, color: 'text.secondary' }}
      >
        View underlying monthly figures
      </Button>
      <Collapse in={showTable}>
        <Table size="small" sx={{ mt: 1, '& td, & th': { fontSize: 12.5 } }}>
          <TableHead>
            <TableRow>
              <TableCell>Month</TableCell>
              <TableCell align="right">Backlog rate</TableCell>
              <TableCell align="right">At-risk qty</TableCell>
              <TableCell align="right">Planned qty</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.ym}>
                <TableCell sx={{ fontFamily: MONO }}>{r.ym}</TableCell>
                <TableCell align="right" sx={{ fontFamily: MONO }}>{r.rate_pct}%</TableCell>
                <TableCell align="right" sx={{ fontFamily: MONO }}>{fmtNum(r.atrisk_qty)}</TableCell>
                <TableCell align="right" sx={{ fontFamily: MONO }}>{fmtNum(r.planned_qty)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Collapse>
    </Panel>
  );
};

/**
 * Paired bars per category: its share of all volume vs. its share of the
 * at-risk backlog. Used for freight terms and packaging type.
 */
const ShareCompare = ({ items, format = (v) => v, overallLabel = 'All volume', overallKey, overallTotalKey, riskKey }) => {
  const theme = useTheme();
  const tk = useTokens();
  const series = [
    { key: 'overall_pct', absKey: overallKey, label: overallLabel, color: tk.accent },
    { key: 'risk_pct', absKey: riskKey, label: 'At-risk backlog', color: tk.accent2 },
  ];
  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 1.5, fontSize: 12, color: 'text.secondary' }}>
        {series.map((s) => (
          <Box key={s.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: s.color }} />
            {s.label}
          </Box>
        ))}
      </Box>
      {items.map((d) => (
        <Box key={d.name} sx={{ mb: 1.25 }}>
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 0.4 }}>{format(d.name)}</Typography>
          {series.map((s) => (
            <Box
              key={s.key}
              sx={{ display: 'grid', gridTemplateColumns: '1fr 150px', gap: 1.25, alignItems: 'center', mb: 0.4 }}
            >
              <Box sx={{ height: 10, borderRadius: '3px', bgcolor: alpha(theme.palette.text.secondary, 0.08) }}>
                <MuiTooltip
                  arrow
                  title={
                    `${format(d.name)} · ${s.label}: ${fmtNum(d[s.absKey])}` +
                    (s.key === 'overall_pct' && overallTotalKey ? ` of ${fmtNum(d[overallTotalKey])}` : '') +
                    ` (${d[s.key]}%)`
                  }
                >
                  <Box
                    sx={{
                      height: '100%',
                      width: `${Math.max(d[s.key], 0.4)}%`,
                      bgcolor: s.color,
                      borderRadius: '3px',
                    }}
                  />
                </MuiTooltip>
              </Box>
              <Typography sx={{ fontSize: 11.5, fontFamily: MONO, textAlign: 'right' }}>
                {fmtNum(d[s.absKey])} ({d[s.key]}%)
              </Typography>
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
};

const useAgingColor = () => {
  const tk = useTokens();
  return (bucket) =>
    bucket.startsWith('0-30')
      ? tk.good
      : bucket.startsWith('31-90')
      ? tk.warning
      : bucket.startsWith('91-180')
      ? tk.serious
      : tk.critical;
};

const CardGrid = ({ cards }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
    {cards.map((c) => (
      <Panel key={c.title} sx={c.full ? { gridColumn: { md: '1 / -1' } } : undefined}>
        <PanelHead title={c.title} caption={c.caption} />
        {c.body}
      </Panel>
    ))}
  </Box>
);

const Breakdowns = ({ a }) => {
  const agingColor = useAgingColor();
  const { data: d, meta } = a;
  const toItems = (list, fmt = (x) => x) => list.map((x) => ({ label: fmt(x.name) || 'Unassigned', v: x.v, n: x.n }));
  // Packaging types with a visible share either way; the long tail is noise.
  const materials = d.packaging_material.filter((m) => m.risk_pct >= 0.1 || m.overall_pct >= 1);

  return (
    <CardGrid
      cards={[
        {
          title: 'Age of at-risk backlog',
          caption: 'Days since required completion date, by outstanding quantity',
          body: (
            <BarList
              items={d.aging_buckets.map((b) => ({ label: b.bucket, v: b.v, n: b.n }))}
              colors={d.aging_buckets.map((b) => agingColor(b.bucket))}
            />
          ),
        },
        {
          title: 'At-risk volume by marketer',
          caption: `By outstanding quantity, top ${d.top_marketers.length} of ${meta.affected.marketers} affected`,
          body: <BarList items={toItems(d.top_marketers)} />,
        },
        {
          title: 'Top destinations at risk',
          caption: `By outstanding quantity, top ${d.top_destinations.length} of ${meta.affected.destinations} affected`,
          body: <BarList items={toItems(d.top_destinations)} />,
        },
        {
          title: 'Top customers at risk',
          caption: `By outstanding quantity, top ${d.top_customers.length} of ${meta.affected.customers} affected`,
          body: <BarList items={toItems(d.top_customers)} />,
        },
        {
          title: 'Freight terms: share of risk vs. share of volume',
          caption: `Lines on each Incoterm (of ${fmtNum(d.incoterm_compare[0].overall_total_n)}) vs. at-risk units on it (of ${fmtNum(d.atrisk_qty)})`,
          body: (
            <ShareCompare
              items={d.incoterm_compare}
              overallLabel="All shipments"
              overallKey="overall_n"
              overallTotalKey="overall_total_n"
              riskKey="risk_v"
            />
          ),
        },
        {
          title: 'Packaging type: share of risk vs. share of volume',
          caption: `Planned units per material (of ${fmtNum(d.packaging_total_qty)}) vs. at-risk units (of ${fmtNum(d.atrisk_qty)})`,
          body: (
            <ShareCompare
              items={materials}
              format={(n) => n.charAt(0) + n.slice(1).toLowerCase()}
              overallKey="overall_qty"
              riskKey="v"
            />
          ),
        },
        {
          title: 'Packing formats with the most stuck quantity',
          caption: `Top ${d.packaging_formats.length} formats by outstanding quantity`,
          full: true,
          body: <BarList items={toItems(d.packaging_formats, packingLabel)} />,
        },
      ]}
    />
  );
};

const Pipeline = ({ a }) => {
  const theme = useTheme();
  const tk = useTokens();
  const agingColor = useAgingColor();
  const { pipeline, production_backlog: pb } = a.data;
  const max = Math.max(...pipeline.map((s) => s.n));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Panel>
        <PanelHead
          title="Where every master-plan line is"
          caption="Lines by the furthest stage reached. The red part of each bar is already past its required date."
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.4 }}>
          {pipeline.map((s) => {
            const w = (s.n / max) * 100;
            const odShare = s.n ? s.overdue_n / s.n : 0;
            return (
              <Box
                key={s.stage}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '210px 1fr 150px' },
                  gap: { xs: 0.5, sm: 1.5 },
                  alignItems: 'center',
                }}
              >
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{s.stage}</Typography>
                <Box sx={{ height: 18, borderRadius: '4px', bgcolor: alpha(theme.palette.text.secondary, 0.08) }}>
                  <MuiTooltip
                    arrow
                    title={`${s.stage}: ${fmtNum(s.n)} lines · ${fmtNum(s.overdue_n)} overdue · ${fmtNum(s.qty)} planned units`}
                  >
                    <Box
                      sx={{
                        width: `${Math.max(w, 0.8)}%`,
                        height: '100%',
                        display: 'flex',
                        borderRadius: '4px',
                        overflow: 'hidden',
                      }}
                    >
                      <Box sx={{ width: `${odShare * 100}%`, bgcolor: tk.critical }} />
                      <Box sx={{ flex: 1, bgcolor: s.stage === 'Packed' ? tk.good : tk.accent }} />
                    </Box>
                  </MuiTooltip>
                </Box>
                <Typography sx={{ fontSize: 12.5, fontFamily: MONO, textAlign: { sm: 'right' } }}>
                  {fmtNum(s.n)}
                  {s.overdue_n > 0 && (
                    <Box component="span" sx={{ color: tk.critical }}>
                      {' '}
                      · {fmtNum(s.overdue_n)} late
                    </Box>
                  )}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Panel>

      <CardGrid
        cards={[
          {
            title: 'Production backlog by warehouse',
            caption: `${fmtNum(pb.n)} booked lines with no production order, past required date`,
            body: (
              <BarList
                items={pb.by_warehouse.map((w) => ({ label: w.name, v: w.n }))}
                format={fmtNum}
                valueLabel="Lines"
              />
            ),
          },
          {
            title: 'Age of production backlog',
            caption: 'Days since required date, by number of lines',
            body: (
              <BarList
                items={pb.aging.map((b) => ({ label: b.bucket, v: b.n }))}
                colors={pb.aging.map((b) => agingColor(b.bucket))}
                format={fmtNum}
                valueLabel="Lines"
              />
            ),
          },
        ]}
      />
    </Box>
  );
};

const LateToPort = ({ a }) => {
  const theme = useTheme();
  const tk = useTokens();
  const ltp = a.data.late_to_port;
  const axis = {
    tick: { fill: theme.palette.text.secondary, fontSize: 11 },
    stroke: alpha(theme.palette.text.secondary, 0.3),
    tickLine: false,
  };
  return (
    <CardGrid
      cards={[
        {
          title: 'Share of packed lines reaching port late, by month',
          caption: `Port cargo date after required date, grouped by required month · overall ${fmtPct(ltp.late_pct)}`,
          body: (
            <Box sx={{ width: '100%', height: 240 }}>
              <ResponsiveContainer>
                <LineChart data={ltp.monthly} margin={{ top: 10, right: 12, left: -8, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke={alpha(theme.palette.text.secondary, 0.15)} />
                  <XAxis dataKey="ym" tickFormatter={ymShort} {...axis} interval={3} />
                  <YAxis domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} tickFormatter={(v) => `${v}%`} {...axis} axisLine={false} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 8,
                      fontSize: 12,
                      color: theme.palette.text.primary,
                    }}
                    labelFormatter={ymLong}
                    formatter={(v, _n, { payload }) => [
                      `${fmtNum(payload.late_n)} of ${fmtNum(payload.n)} lines (${v}%)`,
                      'Late to port',
                    ]}
                  />
                  <ReferenceLine y={50} stroke={alpha(theme.palette.text.secondary, 0.4)} strokeDasharray="4 4" />
                  <Line
                    type="monotone"
                    dataKey="late_pct"
                    stroke={tk.accent2}
                    strokeWidth={2.25}
                    dot={{ r: 2.5, fill: tk.accent2, strokeWidth: 0 }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          ),
        },
        {
          title: 'Late to port by warehouse',
          caption: 'Share of packed lines reaching port after required date (sites with 50+ lines)',
          body: (
            <BarList
              items={ltp.by_warehouse.map((w) => ({
                label: w.name,
                v: w.late_pct,
                n: w.n,
                text: `${fmtNum(w.late_n)} / ${fmtNum(w.n)} (${w.late_pct}%)`,
              }))}
              valueLabel="Late"
              nLabel="Packed lines"
              max={100}
            />
          ),
        },
      ]}
    />
  );
};

const DRILL_COLUMNS = [
  { key: 'sc_no', label: 'SC No', mono: true },
  { key: 'customer_name', label: 'Customer', strong: true },
  { key: 'item_description', label: 'Item' },
  { key: 'final_destination', label: 'Destination' },
  { key: 'warehouse', label: 'Warehouse' },
  { key: 'marketer', label: 'Marketer' },
  { key: 'pending_crd', label: 'Required date', mono: true },
  { key: 'days_overdue', label: 'Days overdue', numeric: true },
  { key: 'bal_to_pack', label: 'Qty outstanding', numeric: true },
  { key: 'shipment_basis', label: 'Terms' },
];

const DrillDown = ({ rows }) => {
  const theme = useTheme();
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState({ key: 'bal_to_pack', dir: -1 });
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(25);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = rows.filter(
      (r) =>
        !q ||
        [r.customer_name, r.final_destination, r.sc_no, r.marketer, r.warehouse, r.item_description]
          .join(' ')
          .toLowerCase()
          .includes(q)
    );
    const { key, dir } = sort;
    return filtered.slice().sort((x, y) => {
      let av = x[key] ?? '';
      let bv = y[key] ?? '';
      if (typeof av === 'string') {
        av = av.toLowerCase();
        bv = String(bv).toLowerCase();
      }
      return av < bv ? -dir : av > bv ? dir : 0;
    });
  }, [rows, query, sort]);

  const pageRows = shown.slice(page * perPage, page * perPage + perPage);

  const toggleSort = (col) => {
    setPage(0);
    setSort((s) =>
      s.key === col.key ? { key: col.key, dir: -s.dir } : { key: col.key, dir: col.numeric ? -1 : 1 }
    );
  };

  return (
    <Panel sx={{ p: 0, overflow: 'hidden' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          flexWrap: 'wrap',
          p: 1.75,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <TextField
          size="small"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setPage(0);
          }}
          placeholder="Search customer, item, destination, SC no…"
          sx={{ flex: '1 1 260px', maxWidth: 380, '& .MuiOutlinedInput-root': { borderRadius: RADIUS.inset } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Typography sx={{ fontSize: 12.5, color: 'text.secondary', fontFamily: MONO }}>
          {fmtNum(shown.length)} of {fmtNum(rows.length)} at-risk lines
        </Typography>
      </Box>
      <Box sx={{ overflowX: 'auto', maxHeight: 620 }}>
        <Table size="small" stickyHeader sx={{ minWidth: 1180, '& td, & th': { fontSize: 12.5, whiteSpace: 'nowrap' } }}>
          <TableHead>
            <TableRow>
              {DRILL_COLUMNS.map((c) => (
                <TableCell
                  key={c.key}
                  align={c.numeric ? 'right' : 'left'}
                  sx={{ fontWeight: 700, bgcolor: theme.palette.background.paper }}
                >
                  <TableSortLabel
                    active={sort.key === c.key}
                    direction={sort.key === c.key && sort.dir === 1 ? 'asc' : 'desc'}
                    onClick={() => toggleSort(c)}
                  >
                    {c.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {pageRows.map((r, i) => (
              <TableRow key={`${r.sc_no}-${r.si_no}-${page}-${i}`} hover>
                <TableCell sx={{ fontFamily: MONO }}>{r.sc_no}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{r.customer_name}</TableCell>
                <TableCell
                  title={r.item_description}
                  sx={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', color: 'text.secondary' }}
                >
                  {r.item_description}
                </TableCell>
                <TableCell>{r.final_destination}</TableCell>
                <TableCell>{r.warehouse}</TableCell>
                <TableCell>{r.marketer}</TableCell>
                <TableCell sx={{ fontFamily: MONO }}>{r.pending_crd}</TableCell>
                <TableCell align="right">
                  <Tag tone={ageTone(r.days_overdue)}>{r.days_overdue}d</Tag>
                </TableCell>
                <TableCell align="right" sx={{ fontFamily: MONO }}>
                  {fmtNum(r.bal_to_pack)}
                </TableCell>
                <TableCell>
                  <Tag tone="muted">{r.shipment_basis || '—'}</Tag>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      <TablePagination
        component="div"
        count={shown.length}
        page={Math.min(page, Math.max(0, Math.ceil(shown.length / perPage) - 1))}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={perPage}
        rowsPerPageOptions={[25, 50, 100]}
        onRowsPerPageChange={(e) => {
          setPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        sx={{ borderTop: `1px solid ${theme.palette.divider}` }}
      />
    </Panel>
  );
};

const KpiGroup = ({ title, caption, items }) => (
  <Box>
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.25, flexWrap: 'wrap', mb: 1.25 }}>
      <SectionTitle sx={{ mb: 0 }}>{title}</SectionTitle>
      <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{caption}</Typography>
    </Box>
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 1.5 }}>
      {items.map((k) => (
        <Kpi key={k.label} {...k} />
      ))}
    </Box>
  </Box>
);

// ---------- shell ----------

const PalmtopDashboard = ({ open, onClose }) => {
  const theme = useTheme();

  // Static snapshot (master_plan.db is never reloaded): pure derivation, no
  // fetch, no loading state.
  const a = useMemo(() => buildPalmtopAnalytics(), []);

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { bgcolor: theme.palette.background.default } }}
    >
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', minHeight: { xs: 52, sm: 60 }, gap: 1 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap sx={{ fontSize: 17, fontWeight: 700, lineHeight: 1.2 }}>
              Palmtop Operations Overview
            </Typography>
            <Typography noWrap sx={{ fontSize: 11.5, color: theme.palette.text.secondary }}>
              Master Plan · snapshot {fmtDate(a.meta.snapshotDate)}
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: theme.palette.text.primary }}>
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ overflowY: 'auto' }}>
        <Box
          sx={{
            maxWidth: 1180,
            mx: 'auto',
            px: { xs: 2, sm: 3 },
            pt: { xs: 3, sm: 5 },
            pb: 10,
            display: 'flex',
            flexDirection: 'column',
            gap: 3.5,
          }}
        >
          <Header a={a} />

          {a.kpiGroups.map((g) => (
            <KpiGroup key={g.title} {...g} />
          ))}

          <ConcentrationStrip a={a} />
          <Briefing a={a} />

          <Box>
            <SectionTitle>Packing backlog trend</SectionTitle>
            <TrendChart a={a} />
          </Box>

          <Box>
            <SectionTitle>Where the packing backlog sits</SectionTitle>
            <Breakdowns a={a} />
          </Box>

          <Box>
            <SectionTitle>Order pipeline</SectionTitle>
            <Pipeline a={a} />
          </Box>

          <Box>
            <SectionTitle>Late to port</SectionTitle>
            <LateToPort a={a} />
          </Box>

          <Box>
            <SectionTitle>Drill-down</SectionTitle>
            <DrillDown rows={a.data.drilldown} />
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default PalmtopDashboard;
